import {
  useRef,
  type DragEvent,
  type MouseEvent,
  type PointerEvent,
} from "react";

const DRAG_THRESHOLD = 5;

type DragState = {
  isPointerDown: boolean;
  moved: boolean;
  pointerId: number | null;
  scrollLeft: number;
  startX: number;
};

export function useHorizontalDragScroll<T extends HTMLElement>() {
  const dragState = useRef<DragState>({
    isPointerDown: false,
    moved: false,
    pointerId: null,
    scrollLeft: 0,
    startX: 0,
  });
  const suppressClick = useRef(false);
  const scrollRef = useRef<T | null>(null);

  const endDrag = (target: T) => {
    const state = dragState.current;

    target.classList.remove("is-dragging");
    state.isPointerDown = false;
    state.pointerId = null;
  };

  const onPointerDown = (event: PointerEvent<T>) => {
    if (event.button !== 0 || event.pointerType !== "mouse") return;

    const target = event.currentTarget;
    if (target.scrollWidth <= target.clientWidth) return;

    dragState.current = {
      isPointerDown: true,
      moved: false,
      pointerId: event.pointerId,
      scrollLeft: target.scrollLeft,
      startX: event.clientX,
    };

    target.classList.add("is-dragging");
  };

  const onPointerMove = (event: PointerEvent<T>) => {
    const state = dragState.current;
    if (!state.isPointerDown || state.pointerId !== event.pointerId) return;

    const moveX = event.clientX - state.startX;
    if (Math.abs(moveX) > DRAG_THRESHOLD) {
      state.moved = true;
      suppressClick.current = true;
    }

    if (!state.moved) return;

    // 드래그 중 발생하는 텍스트 선택과 하위 요소 클릭을 막고 스크롤 이동만 반영한다.
    event.preventDefault();
    event.currentTarget.scrollLeft = state.scrollLeft - moveX;
  };

  const onPointerUp = (event: PointerEvent<T>) => {
    const state = dragState.current;
    if (!state.isPointerDown || state.pointerId !== event.pointerId) return;

    endDrag(event.currentTarget);
  };

  const onPointerCancel = (event: PointerEvent<T>) => {
    endDrag(event.currentTarget);
  };

  const onPointerLeave = (event: PointerEvent<T>) => {
    if (!dragState.current.isPointerDown) return;

    endDrag(event.currentTarget);
  };

  const onClickCapture = (event: MouseEvent<T>) => {
    if (!suppressClick.current) return;

    suppressClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  const onDragStart = (event: DragEvent<T>) => {
    event.preventDefault();
  };

  return {
    dragScrollHandlers: {
      onClickCapture,
      onDragStart,
      onPointerCancel,
      onPointerDown,
      onPointerLeave,
      onPointerMove,
      onPointerUp,
    },
    scrollRef,
  };
}
